import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DataGrid,
  gridPageCountSelector,
  gridPageSelector,
  useGridApiContext,
  useGridApiRef,
  useGridSelector,
} from "@mui/x-data-grid";
import Pagination from "@mui/material/Pagination";
import { Button } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import classes from "../LandedCostUploadHistoryTable/LandedCostUploadHistory.module.css";
import { updateLandedCostAntiDumpingHistory } from "../../API/landedCostAntiDumpingHistoryUpdateApi";
import AlertManager from "../Alert/Alert";
import { normalizeCurrencyValue } from "../utils/currency";
import { exportRowsToXlsx } from "../utils/exportUtils";

const percentFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const RATE_LIMIT = 10000000;

export const formatCurrencyValue = (value) => {
  const normalized = normalizeCurrencyValue(value);
  if (normalized === null) {
    return typeof value === "string" ? value : "";
  }
  return `${percentFormatter.format(normalized)}%`;
};

const normalizeRateValue = (value) => normalizeCurrencyValue(value);

const PercentEditCell = (params) => {
  const { id, field, value } = params;
  const handleChange = (event) => {
    const raw = event.target.value;
    params.api.setEditCellValue({ id, field, value: raw }, event);
  };
  return (
    <input
      type="number"
      step="0.01"
      value={value ?? ""}
      onChange={handleChange}
      style={{ width: "100%", height: "100%", boxSizing: "border-box", padding: "6px" }}
      data-testid={`${field}-edit-${id}`}
    />
  );
};

const LandedCostAntiDumpingTable = ({ rows = [], loading = false }) => {
  const [pageSize, setPageSize] = useState(15);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitArmed, setSubmitArmed] = useState(false);
  const dirtyRowsRef = useRef(new Map());
  const apiRef = useGridApiRef();

  const [gridRows, setGridRows] = useState(Array.isArray(rows) ? rows : []);

  useEffect(() => {
    setGridRows(Array.isArray(rows) ? rows : []);
    dirtyRowsRef.current.clear();
    setIsEditing(false);
    setSubmitArmed(false);
  }, [rows]);

  const reloadAfterAlert = useCallback((alertPromise) => {
    if (alertPromise && typeof alertPromise.finally === "function") {
      alertPromise.finally(() => window.location.reload());
    }
  }, []);

  const clampRate = useCallback((value) => {
    const numeric = normalizeCurrencyValue(value);
    if (numeric === null || !Number.isFinite(numeric)) return null;
    if (numeric < -RATE_LIMIT) return -RATE_LIMIT;
    if (numeric > RATE_LIMIT) return RATE_LIMIT;
    return numeric;
  }, []);

  const isPartialDecimal = useCallback(
    (value) => typeof value === "string" && /^-?\d*\.?\d*$/.test(value.trim()),
    []
  );

  const normalizeRowRates = useCallback(
    (row) => ({
      ...row,
      antiDumpingRate: clampRate(row.antiDumpingRate) ?? row.antiDumpingRate,
      counterVailingRate: clampRate(row.counterVailingRate) ?? row.counterVailingRate,
    }),
    [clampRate]
  );

  const markRowDirty = useCallback((newRow, oldRow) => {
    if (!newRow || !oldRow) return;
    const changed =
      newRow.napaSku !== oldRow.napaSku ||
      newRow.hqLine !== oldRow.hqLine ||
      newRow.antiDumpingRate !== oldRow.antiDumpingRate ||
      newRow.counterVailingRate !== oldRow.counterVailingRate;

    if (changed) {
      dirtyRowsRef.current.set(newRow.id, newRow);
    } else {
      dirtyRowsRef.current.delete(newRow.id);
    }
  }, []);

  const handleProcessRowUpdate = useCallback(
    (newRow, oldRow) => {
      if (!isEditing) return oldRow;

      const normalizedAnti = clampRate(newRow.antiDumpingRate);
      const normalizedCounter = clampRate(newRow.counterVailingRate);
      const invalidAnti = normalizedAnti === null && !isPartialDecimal(newRow.antiDumpingRate);
      const invalidCounter = normalizedCounter === null && !isPartialDecimal(newRow.counterVailingRate);

      if (invalidAnti || invalidCounter) {
        AlertManager.error("Rates must be between -10000000 and 10000000.", { contactSupport: false });
        return oldRow;
      }

      const nextRow = normalizeRowRates({
        ...newRow,
        antiDumpingRate: normalizedAnti ?? newRow.antiDumpingRate,
        counterVailingRate: normalizedCounter ?? newRow.counterVailingRate,
      });

      setGridRows((prev) =>
        prev.map((row) => (row.id === nextRow.id ? nextRow : row))
      );
      markRowDirty(nextRow, oldRow);
      return nextRow;
    },
    [clampRate, isEditing, isPartialDecimal, markRowDirty, normalizeRowRates]
  );

  const commitActiveCellEdits = useCallback(async () => {
    const editModel =
      apiRef.current?.getEditRowsModel?.() || apiRef.current?.state?.editRows || {};
    const entries = Object.entries(editModel || {});
    for (const [rowId, fields] of entries) {
      const numericId = Number(rowId);
      const resolvedId = Number.isNaN(numericId) ? rowId : numericId;
      for (const field of Object.keys(fields)) {
        try {
          await Promise.resolve(
            apiRef.current?.commitCellChange?.({ id: resolvedId, field })
          );
          await Promise.resolve(
            apiRef.current?.stopCellEditMode?.({
              id: resolvedId,
              field,
              ignoreModifications: false,
            })
          );
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("Unable to commit anti dumping edit", err);
        }
      }
    }
  }, [apiRef]);

  const handleEditToggle = useCallback(async () => {
    if (!isEditing && gridRows.length === 0) return;

    if (!isEditing) {
      dirtyRowsRef.current.clear();
      setIsEditing(true);
      setSubmitArmed(false);
      return;
    }

    if (saving) return;

    await commitActiveCellEdits();

    const payload = Array.from(dirtyRowsRef.current.values()).map((row) =>
      normalizeRowRates({
        ...row,
        antiDumpingRate: normalizeRateValue(row.antiDumpingRate),
        counterVailingRate: normalizeRateValue(row.counterVailingRate),
      })
    );
    if (!payload.length) {
      setIsEditing(false);
      setSubmitArmed(false);
      return;
    }

    try {
      await AlertManager.confirm(`Save ${payload.length} edited row(s)?`, {
        contactSupport: false,
        contactSupportBtn: true,
        closeBtnText: "Save",
        contactBtnText: "Cancel",
      });
    } catch (err) {
      return;
    }

    setSaving(true);
    try {
      const response = await updateLandedCostAntiDumpingHistory(payload);
      if (!response) {
        throw new Error("Anti dumping update failed");
      }
      dirtyRowsRef.current.clear();
      setIsEditing(false);
      setSubmitArmed(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to save anti dumping rows", error);
      reloadAfterAlert(AlertManager.error("Failed to update anti dumping history."));
    } finally {
      setSaving(false);
    }
  }, [
    commitActiveCellEdits,
    gridRows.length,
    isEditing,
    normalizeRowRates,
    reloadAfterAlert,
    saving,
  ]);

  const handleDummySubmit = useCallback(async () => {
    await commitActiveCellEdits();
    setSubmitArmed(true);
  }, [commitActiveCellEdits]);

  const columns = useMemo(
    () => [
      { field: "napaSku", headerName: "Parsed SKU #", flex: 1.2, minWidth: 140},
      { field: "hqLine", headerName: "HQ Line", flex: 1, minWidth: 140},
      {
        field: "antiDumpingRate",
        headerName: "Anti Dumping (%)",
        flex: 1,
        minWidth: 150,
        editable: true,
        valueFormatter: ({ value }) => formatCurrencyValue(value),
        preProcessEditCellProps: (params) => {
          const raw = params.props?.value;
          const numeric = clampRate(raw);
          const isValid =
            (numeric !== null && numeric >= -RATE_LIMIT && numeric <= RATE_LIMIT) || isPartialDecimal(raw);
          return { ...params.props, error: !isValid };
        },
        valueParser: (value, params) => {
          const numeric = clampRate(value);
          if (numeric === null) {
            if (isPartialDecimal(value)) {
              return value;
            }
            return params?.row?.antiDumpingRate ?? "";
          }
          return numeric;
        },
        renderEditCell: (params) => <PercentEditCell {...params} />,
      },
      {
        field: "counterVailingRate",
        headerName: "Counter Vailing (%)",
        flex: 1.2,
        minWidth: 170,
        editable: true,
        valueFormatter: ({ value }) => formatCurrencyValue(value),
        preProcessEditCellProps: (params) => {
          const raw = params.props?.value;
          const numeric = clampRate(raw);
          const isValid =
            (numeric !== null && numeric >= -RATE_LIMIT && numeric <= RATE_LIMIT) || isPartialDecimal(raw);
          return { ...params.props, error: !isValid };
        },
        valueParser: (value, params) => {
          const numeric = clampRate(value);
          if (numeric === null) {
            if (isPartialDecimal(value)) {
              return value;
            }
            return params?.row?.counterVailingRate ?? "";
          }
          return numeric;
        },
        renderEditCell: (params) => <PercentEditCell {...params} />,
      },
    ],
    [clampRate, isPartialDecimal]
  );

  const handleExport = useCallback(() => {
    exportRowsToXlsx({
      columns,
      rows: gridRows,
      fileName: "anti-dumping-history.xlsx",
      sheetName: "Anti Dumping",
      warningMessage: "No anti dumping rows to export.",
    });
  }, [columns, gridRows]);

  const CustomPagination = () => {
    const apiRef = useGridApiContext();
    const page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <Pagination
        color="primary"
        count={pageCount}
        page={page + 1}
        onChange={(_, value) => apiRef.current.setPage(value - 1)}
      />
    );
  };

  return (
    <div className={classes.wrapper}>
      <div className={classes.tableActions}>
        <Button
          variant="contained"
          size="small"
          onClick={handleEditToggle}
          disabled={saving || (isEditing && !submitArmed) || gridRows.length === 0}
          className={classes.editButton}
        >
          {saving ? "Saving..." : isEditing ? "Save Changes" : "Edit"}
        </Button>
        {isEditing && (
          <Button
            type="submit"
            size="small"
            variant="text"
            className={classes.dummySubmitBtn}
            data-testid="anti-dumping-dummy-submit"
            onClick={handleDummySubmit}
          >
            Finish Editing
          </Button>
        )}
        <Button
          variant="contained"
          size="small"
          onClick={handleExport}
          disabled={!gridRows.length || loading || saving || isEditing}
          startIcon={<DownloadIcon fontSize="small" />}
          className={classes.exportButton}
          data-testid="anti-dumping-export-btn"
        >
          Export
        </Button>
      </div>
      <DataGrid
        rows={gridRows}
        columns={columns}
        apiRef={apiRef}
        disableRowSelectionOnClick
        hideFooterSelectedRowCount
        pageSize={pageSize}
        rowsPerPageOptions={[15, 25, 50]}
        onPageSizeChange={(size) => setPageSize(size)}
        editMode="cell"
        autoHeight
        isCellEditable={() => isEditing}
        processRowUpdate={handleProcessRowUpdate}
        experimentalFeatures={{ newEditingApi: true }}
        onProcessRowUpdateError={(error) => console.error(error)}
        rowHeight={32}
        columnHeaderHeight={36}
        loading={loading || saving}
        components={{ Pagination: CustomPagination }}
        sx={{
          "& .MuiDataGrid-cell": {
            fontSize: "0.78rem",
            py: 0.25,
          },
          "& .MuiDataGrid-columnHeaders": {
            fontSize: "0.82rem",
          },
        }}
      />
    </div>
  );
};

export default LandedCostAntiDumpingTable;
